import { FC, PropsWithChildren, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Button, Grid, Typography, CircularProgress } from '@mui/material'
import {
  HasSidebarLayout,
  DataLoad,
  CollapsibleSection,
  EntityHeader,
  SensorInfo,
  PropsInjector,
  TabGroupFilter,
  ProjectsCurrentProject,
  SensorChartTabs,
  SensorLastReadings,
  SensorPastProjects,
  SensorStagingLabel,
  StatefulDialog,
  AssignSensorToContractor,
  SensorBackupUplinks,
  SensorArchivedLabel,
  MakeAvailableSensorModal,
  MarkLostSensorModal,
  DownloadMeasurementsModal
} from 'src/components'
import { useAssignSensorRequest, useSensorRequest, useSensorStatesRequest } from 'src/hooks/api'
import { useDialog, useQRCode, useSensorLabel, useSensorStateUpdate } from 'src/hooks/ui'
import { Sensor, SensorState } from 'src/api'
import { DialogNames, SearchParamsKeys, SensorStateList } from 'src/enums'
import { capitalizeFirstLetter } from 'src/utils/formatters'
import { SensorEvents } from 'src/components/sections/sensor/sensorEvents'
import { SensorGateways } from 'src/components/sections/sensor/sensorGateways'

interface LoRaWanSensorShowContentProps {
  sensor?: Sensor
  refetchSensor?: () => void
  sensorStates?: SensorState[]
}
interface SensorStatusProps {
  sensor: Sensor
  sensorStates: SensorState[]
  updateState: (payload: { uniqueId: string; state: SensorState; onSuccess?: () => void; onError?: () => void }) => void
  openDialog: () => void
  refetchSensor?: () => void
}

const SensorStatus: FC<SensorStatusProps> = ({ sensor, sensorStates, updateState, openDialog, refetchSensor }) => {
  const getNextAction: { action: () => void } = useCallback(() => {
    const currentState: string = sensor?.state?.name || ''
    let _nextAction: () => void = () => {}
    switch (currentState) {
      case 'new':
        const nextState = sensorStates.find((state) => state.name === 'available')
        _nextAction = () =>
          updateState({
            uniqueId: sensor.uniqueId as string,
            state: nextState as SensorState,
            onSuccess: refetchSensor
          })
        break
      case 'available':
      case 'assigned':
        _nextAction = openDialog
        break
      default:
        break
    }
    return {
      action: _nextAction
    }
  }, [sensor, sensorStates, updateState, openDialog, refetchSensor])()

  const updateStateButton = () => {
    const labelMap: Record<string, string> = {
      new: 'Make Ready to use',
      available: 'Assign',
      assigned: 'Reassign'
    }
    const actionLabel: string = labelMap[sensor?.state?.name || '']

    return actionLabel ? `(${actionLabel})` : ''
  }

  return (
    <Typography variant="h6">
      {capitalizeFirstLetter(sensor.state?.name || '')}{' '}
      {updateStateButton() ? (
        <Button onClick={getNextAction.action} variant="text">
          {updateStateButton()}
        </Button>
      ) : null}
    </Typography>
  )
}

const getStatusList = ({ projectId, uniqueId }: { projectId: number | null; uniqueId: string }) => {
  return [
    {
      status: 'Current Project',
      content: <ProjectsCurrentProject projectId={projectId} noResultText="Current sensor no belongs to any project" showFooter={false} />
    },
    {
      status: 'Past Projects',
      content: <SensorPastProjects sensorUniqueId={uniqueId} noResultText="Current sensor does not have past projects" showFooter={false} />
    }
  ]
}

const LoRaWanSensorShowContent: FC<LoRaWanSensorShowContentProps> = ({ sensor = null, refetchSensor, sensorStates }) => {
  const { loading: loadingQRCode, generateCode } = useQRCode()
  const { generatingLabel, downloadLabel } = useSensorLabel()
  const { openDialog: openAssignSensorDialog, closeDialog: closeAssignSensorDialog } = useDialog(DialogNames.AssignSensorToContractor)
  const assignSensorRequest = useAssignSensorRequest({
    options: {
      onSettled: () => closeAssignSensorDialog(),
      onSuccess: refetchSensor
    }
  })
  const { openSensorMakeAvailableDialog, closeSensorMakeAvailableDialog, openSensorMarkLostDialog, closeSensorMarkLostDialog, updateSensorState, isLoadingChangingSensorState } =
    useSensorStateUpdate()
  const newState = useMemo(() => (sensorStates || []).find((state) => state.name === SensorStateList.New), [sensorStates])
  const lostState = useMemo(() => (sensorStates || []).find((state) => state.name === SensorStateList.Lost), [sensorStates])
  const isLost = useMemo(() => (row: Sensor) => row && row.stateId === lostState?.id, [lostState])

  if (!sensor) return null

  const { uniqueId, deviceId, sensorModel, projectSectionsSensors, type } = sensor
  const projectId = projectSectionsSensors && projectSectionsSensors.length ? projectSectionsSensors[0].projectId : null
  const projectName = projectSectionsSensors && projectSectionsSensors.length ? projectSectionsSensors[0].project?.name : null

  const assignSensor = (contractorId: number | null) => {
    assignSensorRequest.mutate({
      uniqueId: sensor.uniqueId,
      payload: { contractorId }
    })
  }

  const getRightIcon = (): React.ReactNode => {
    const nodes: React.ReactNode[] = []
    if (sensor.isStagingSensor) {
      nodes.push(<SensorStagingLabel size="medium" key="staging-label" sx={{ mr: 1 }} />)
    }
    if (sensor.state?.name === SensorStateList.Archived) {
      nodes.push(<SensorArchivedLabel size="medium" key="archived-lable" />)
    }
    return nodes
  }

  const title = `${sensorModel?.displayName || 'Sensor'}: ${uniqueId}`
  return (
    <>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <EntityHeader
            title={title}
            rightIcon={getRightIcon()}
            actionBlockOnTheRight={
              <SensorStatus sensor={sensor} sensorStates={sensorStates || []} updateState={updateSensorState} openDialog={openAssignSensorDialog} refetchSensor={refetchSensor} />
            }
          >
            <Grid container spacing={2}>
              <Grid item>
                <Button variant="outlined" size="large" onClick={() => generateCode(deviceId || uniqueId)} sx={{ width: 210 }} disabled={loadingQRCode}>
                  {loadingQRCode ? <CircularProgress size={26} /> : 'Generate QR code'}
                </Button>
              </Grid>
              <Grid item>
                <Button variant="outlined" size="large" onClick={() => downloadLabel(deviceId as string, type?.name)} sx={{ width: 210 }} disabled={generatingLabel}>
                  {generatingLabel ? <CircularProgress size={26} /> : 'Download label'}
                </Button>
              </Grid>
              <Grid item>
                <Button variant="outlined" size="large" onClick={isLost(sensor) ? openSensorMakeAvailableDialog : openSensorMarkLostDialog} sx={{ width: 210 }}>
                  {isLost(sensor) ? 'Make available' : 'Mark as lost'}
                </Button>
              </Grid>
              <Grid item>
                <DownloadMeasurementsModal deviceType="sensor" deviceId={uniqueId} />
              </Grid>
            </Grid>
          </EntityHeader>
        </Grid>
        <Grid container item xs={12} flexDirection="column">
          <Grid container item alignItems="center" spacing={2} mb={1}>
            <Grid item>
              <Typography variant="h6">Sensor Information</Typography>
            </Grid>
          </Grid>
          <Grid item sx={{ width: '100%', minWidth: 0 }}>
            <SensorInfo sensor={sensor} refetchSensor={refetchSensor} />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title={`Current project: ${projectName || 'none'}`}>
            <TabGroupFilter
              name={SearchParamsKeys.Tab}
              statusList={getStatusList({ projectId, uniqueId })}
              paramsToRemove={[SearchParamsKeys.Page, SearchParamsKeys.RowsPerPage]}
            />
          </CollapsibleSection>
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title="Sensor Chart Of Readings">
            <SensorChartTabs sensor={sensor} />
          </CollapsibleSection>
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title="Sensor Last Readings">
            <SensorLastReadings sensor={sensor} />
          </CollapsibleSection>
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title="Events">
            <SensorEvents sensorId={sensor.id} />
          </CollapsibleSection>
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title="Last received raw data">
            <SensorBackupUplinks sensorUniqueId={sensor.uniqueId} />
          </CollapsibleSection>
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title="Sensor Gateways">
            <SensorGateways sensor={sensor} />
          </CollapsibleSection>
        </Grid>
      </Grid>
      <StatefulDialog name={DialogNames.AssignSensorToContractor} wrapContent={false}>
        <AssignSensorToContractor onSubmit={assignSensor} />
      </StatefulDialog>
      <MakeAvailableSensorModal
        sensorDeviceId={sensor.deviceId as string}
        isLoading={isLoadingChangingSensorState}
        onCancel={() => closeSensorMakeAvailableDialog()}
        onSubmit={() => {
          updateSensorState({
            uniqueId: sensor.uniqueId as string,
            state: newState as SensorState,
            onSuccess: refetchSensor
          })
        }}
      />
      <MarkLostSensorModal
        sensorDeviceId={sensor.deviceId as string}
        isLoading={isLoadingChangingSensorState}
        onCancel={() => closeSensorMarkLostDialog()}
        onSubmit={() => {
          updateSensorState({
            uniqueId: sensor.uniqueId as string,
            state: lostState as SensorState,
            onSuccess: refetchSensor
          })
        }}
      />
    </>
  )
}

const LoRaWanSensorDataLoader: FC<PropsWithChildren<{ sensorUniqueId: string }>> = ({ sensorUniqueId, children }) => {
  const { isInitialLoading, isError, data, error, refetch } = useSensorRequest({
    uniqueId: sensorUniqueId,
    options: {
      notifyOnChangeProps: ['isLoading', 'isError', 'isRefetching']
    }
  })
  document.title = `CLīMIT Admin Panel - LoRaWan Sensor ${data?.deviceId || ''}`
  const { data: sensorStateData } = useSensorStatesRequest()

  const props = {
    sensor: data || null,
    refetchSensor: refetch,
    sensorStates: sensorStateData || []
  }

  return (
    <DataLoad isLoading={isInitialLoading} isError={isError} errorMessage={error?.message}>
      <PropsInjector props={props}>{children}</PropsInjector>
    </DataLoad>
  )
}

const LoRaWanSensorShow: FC = () => {
  const params = useParams()
  const { uniqueId } = params as { uniqueId: string }

  return (
    <HasSidebarLayout>
      <LoRaWanSensorDataLoader sensorUniqueId={uniqueId}>
        <LoRaWanSensorShowContent />
      </LoRaWanSensorDataLoader>
    </HasSidebarLayout>
  )
}

export default LoRaWanSensorShow
